import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  IconArrowLeft,
  IconMail,
  IconLock,
  IconTrash,
  IconLoader2,
  IconAlertTriangle,
  IconCheck,
  IconEye,
  IconEyeOff,
  IconUser,
  IconCrown
} from '@tabler/icons-react';

const Msg = ({ msg }) => {
  if (!msg) return null;
  const isError = msg.type === 'error';
  return (
    <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${
      isError ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'
    }`}>
      {isError
        ? <IconAlertTriangle className="w-4 h-4 flex-shrink-0 text-red-400" />
        : <IconCheck className="w-4 h-4 flex-shrink-0 text-green-400" />}
      <p className={`text-sm ${isError ? 'text-red-400' : 'text-green-400'}`}>{msg.text}</p>
    </div>
  );
};

const SettingsPage = () => {
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Смена email
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailStep, setEmailStep] = useState('input');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);

  // Смена пароля
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [passMsg, setPassMsg] = useState(null);

  // Удаление аккаунта
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [deleteDeadline, setDeleteDeadline] = useState(null);

  useEffect(() => {
    fetch('/api/auth/settings', { credentials: 'include' })
      .then((r) => {
        if (r.status === 401) {
          navigate('/login?redirect=/settings');
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data) setSettings(data);
      })
      .catch(() => setSettings(null))
      .finally(() => setLoadingSettings(false));
  }, [navigate]);

  const handleRequestEmailChange = async () => {
    setEmailMsg(null);
    if (!newEmail.trim()) {
      setEmailMsg({ type: 'error', text: 'Введите новый email' });
      return;
    }
    setEmailLoading(true);
    try {
      const res = await fetch('/api/auth/settings/change-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ new_email: newEmail.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.errorMessage);
      setEmailStep('code');
      setEmailMsg({ type: 'success', text: 'Код отправлен на новый email' });
    } catch (e) {
      setEmailMsg({ type: 'error', text: e.message });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleConfirmEmailChange = async () => {
    setEmailMsg(null);
    setEmailLoading(true);
    try {
      const res = await fetch('/api/auth/settings/confirm-email-change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: emailCode.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.errorMessage);
      setEmailMsg({ type: 'success', text: 'Email изменён' });
      setSettings((prev) => ({ ...prev, email: newEmail.trim(), email_verified: true }));
      setEmailStep('input');
      setNewEmail('');
      setEmailCode('');
    } catch (e) {
      setEmailMsg({ type: 'error', text: e.message });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPassMsg(null);
    if (newPass !== confirmPass) {
      setPassMsg({ type: 'error', text: 'Пароли не совпадают' });
      return;
    }
    if (newPass.length < 8) {
      setPassMsg({ type: 'error', text: 'Минимум 8 символов' });
      return;
    }
    setPassLoading(true);
    try {
      const res = await fetch('/api/auth/settings/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_password: currentPass, new_password: newPass })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.errorMessage);
      setPassMsg({ type: 'success', text: 'Пароль изменён' });
      setCurrentPass('');
      setNewPass('');
      setConfirmPass('');
    } catch (e) {
      setPassMsg({ type: 'error', text: e.message });
    } finally {
      setPassLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteMsg(null);
    setDeleteLoading(true);
    try {
      const res = await fetch('/api/auth/settings/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.errorMessage);
      setDeleteDeadline(data.deadline);
      setDeleteMsg({ type: 'success', text: data.message });
    } catch (e) {
      setDeleteMsg({ type: 'error', text: e.message });
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loadingSettings) {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center">
        <IconLoader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center">
        <p className="text-gray-500">Не удалось загрузить настройки</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950 py-12 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white transition mb-8">
          <IconArrowLeft className="w-4 h-4" />
          Назад к панели
        </Link>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Настройки аккаунта</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Левая колонка — информация */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-yellow-400 text-black flex items-center justify-center">
                  <IconUser className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Аккаунт</h2>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-dark-800">
                  <span className="text-gray-600 dark:text-dark-400 text-sm">Имя пользователя</span>
                  <span className="text-gray-900 dark:text-white font-medium">{settings.username}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-dark-800">
                  <span className="text-gray-600 dark:text-dark-400 text-sm">Email</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 dark:text-white font-medium text-right max-w-[140px] truncate">
                      {settings.email || 'Не привязан'}
                    </span>
                    {settings.email_verified ? (
                      <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded-full">Подтверждён</span>
                    ) : settings.email ? (
                      <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-0.5 rounded-full">Не подтверждён</span>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600 dark:text-dark-400 text-sm">План</span>
                  <div className="flex items-center gap-1.5">
                    <IconCrown className="w-4 h-4 text-primary-500" />
                    <span className="text-gray-900 dark:text-white font-medium capitalize">{settings.plan}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Правая колонка — действия */}
          <div className="lg:col-span-2 space-y-6">
            {/* Смена email */}
            <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-yellow-400 text-black flex items-center justify-center">
                  <IconMail className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Смена email</h2>
              </div>

              <Msg msg={emailMsg} />

              {emailStep === 'input' ? (
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Новый email"
                    className="flex-1 px-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                  <button
                    onClick={handleRequestEmailChange}
                    disabled={emailLoading || !newEmail}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {emailLoading ? <IconLoader2 className="w-5 h-5 animate-spin" /> : <>Отправить код</>}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-dark-400">Введите код с {newEmail}</p>
                  <input
                    type="text"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-значный код"
                    className="w-full px-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors text-center text-lg tracking-widest"
                    maxLength={6}
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setEmailStep('input'); setEmailCode(''); setEmailMsg(null); }}
                      className="flex-1 py-3 bg-gray-100 dark:bg-dark-850 hover:bg-gray-200 dark:hover:bg-dark-800 rounded-lg font-medium text-gray-700 dark:text-dark-200 transition"
                    >
                      Назад
                    </button>
                    <button
                      onClick={handleConfirmEmailChange}
                      disabled={emailLoading || emailCode.length !== 6}
                      className="flex-1 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {emailLoading ? <IconLoader2 className="w-5 h-5 animate-spin" /> : <>Подтвердить</>}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Смена пароля */}
            <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-yellow-400 text-black flex items-center justify-center">
                  <IconLock className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Смена пароля</h2>
              </div>

              <Msg msg={passMsg} />

              <div className="space-y-3">
                <div className="relative">
                  <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                  <input
                    type={showCurrentPass ? 'text' : 'password'}
                    value={currentPass}
                    onChange={(e) => setCurrentPass(e.target.value)}
                    placeholder="Текущий пароль"
                    className="w-full pl-10 pr-10 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                  <button type="button" onClick={() => setShowCurrentPass(!showCurrentPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition">
                    {showCurrentPass ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="relative">
                  <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                  <input
                    type={showNewPass ? 'text' : 'password'}
                    value={newPass}
                    onChange={(e) => setNewPass(e.target.value)}
                    placeholder="Новый пароль (мин. 8 символов)"
                    className="w-full pl-10 pr-10 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                  <button type="button" onClick={() => setShowNewPass(!showNewPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition">
                    {showNewPass ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
                  </button>
                </div>
                <input
                  type="password"
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  placeholder="Повторите новый пароль"
                  className="w-full px-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  onClick={handleChangePassword}
                  disabled={passLoading || !currentPass || !newPass || !confirmPass}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passLoading ? <IconLoader2 className="w-5 h-5 animate-spin" /> : <>Изменить пароль</>}
                </button>
              </div>
            </div>

            {/* Удаление аккаунта */}
            <div className="bg-white dark:bg-dark-900 border border-red-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <IconTrash className="w-5 h-5 text-red-500" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Удаление аккаунта</h2>
              </div>

              <p className="text-sm text-gray-600 dark:text-dark-400 mb-4">
                Аккаунт будет удалён через 7 дней. В течение этого времени вы можете восстановить его через письмо на почту.
                <br />
                <a href="https://t.me/XNCsup_bot" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-400 underline">
                  Нужна помощь? Напишите в поддержку
                </a>
              </p>

              <Msg msg={deleteMsg} />

              {deleteDeadline && (
                <p className="text-sm text-yellow-500 mb-4">
                  Аккаунт будет удалён: {new Date(deleteDeadline).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}

              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg font-semibold text-red-500 flex items-center justify-center gap-2 transition"
                >
                  <IconTrash className="w-4 h-4" />
                  Удалить аккаунт
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <IconAlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-500">Вы уверены?</p>
                      <p className="text-xs text-red-400 mt-1">
                        Все туннели будут удалены. Восстановить аккаунт можно будет в течение 7 дней через письмо на почту.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 py-3 bg-gray-100 dark:bg-dark-850 hover:bg-gray-200 dark:hover:bg-dark-800 rounded-lg font-medium text-gray-700 dark:text-dark-200 transition"
                    >
                      Отмена
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleteLoading}
                      className="flex-1 py-3 bg-red-500 hover:bg-red-600 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleteLoading ? <IconLoader2 className="w-5 h-5 animate-spin" /> : <>Да, удалить</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
